import { ConsentLevels, useCookieConsent, useOnWindowResize } from 'db-npm-e4sc-rd-ui';
import deepEqual from 'fast-deep-equal/react';
import throttle from 'lodash/throttle';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { ContextSettings, useContextSettings, useIsEditor } from '../../../shared/hooks';
import * as parsers from './parsers/';

type ContentParserProps = {
  children: ReactNode[] | React.ReactElement;
  /**
   * Config for customizing which parsers to use
   */
  config?: ContentParserConfig;
  /**
   * Specify dependencies e.g. for content not being rendered initially
   */
  dependencies?: any[];
};

export type ContentParserConfig = {
  /**
   * Parsers to run
   */
  useParsers: Parser[];
  /**
   * Throttle (ms) for onResize
   */
  resizeThrottle: number;
};

export type Parser = {
  /**
   * Parser function
   */
  handler: ParserFunctionInterface;
  /**
   * Config for customizing the parser
   */
  config?: any;
  /**
   * Pass current cookie consent level if relevant for parser
   */
  cookieConsentLevels?: ConsentLevels;
};

export interface ParserFunctionInterface {
  (el: any, config?: any, cookieConsentLevels?: ConsentLevels, contextSettings?: ContextSettings): {
    init: () => void;
    onResize?: () => void;
  };
}

export const ContentParser: React.FC<ContentParserProps> = ({
  children,
  config = defaultConfig,
  dependencies = [],
}) => {
  const cookieConsentLevels = useCookieConsent().getConsentLevels();
  const contextSettings = useContextSettings();
  const ref = useRef(null);
  const handlersOnResizeRef = useRef<any[]>([]);
  const isEditor = useIsEditor();

  // Reuse the previous children reference when content is unchanged, so an ancestor
  // recreating the element on every render doesn't reset parser-injected DOM content
  const previousChildrenRef = useRef(children);
  if (!deepEqual(previousChildrenRef.current, children)) {
    previousChildrenRef.current = children;
  }
  const stableChildren = previousChildrenRef.current;

  useEffect(
    () => {
      if (ref.current === null || isEditor) return;

      const handlersOnResize: any[] = [];
      config.useParsers.forEach(function (parser) {
        const handler = parser.handler(ref.current, parser.config, cookieConsentLevels, contextSettings);
        handler.init();
        if (typeof handler.onResize === 'function') {
          handlersOnResize.push(handler);
        }
      });
      handlersOnResizeRef.current = handlersOnResize;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies
  );

  const onResize = useCallback(
    throttle(() => {
      handlersOnResizeRef.current.forEach(function (handler) {
        handler.onResize();
      });
    }, config.resizeThrottle),
    []
  );
  useOnWindowResize(onResize);

  // Cache the element so unrelated re-renders (e.g. consent/resize state) don't make React reconcile `children` and reset parser-injected DOM content
  return <div ref={ref}>{stableChildren}</div>;
};

const defaultConfig: ContentParserConfig = {
  useParsers: [{ handler: parsers.cookiePanel }, { handler: parsers.videoParser }],
  resizeThrottle: 500,
};
